import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown } from 'lucide-react';

const faqs = [
  {
    id: 1,
    q: 'What exactly is Lucknow Chikankari?',
    a: 'Chikankari is a 400+ year old hand embroidery art form from Lucknow, India. Originating in the Mughal era, it involves intricate stitching on fabric using fine needles and cotton/silk threads. With 36+ unique stitch types — from murri and phanda to jaali and bakhiya — every piece is a wearable masterpiece.',
  },
  {
    id: 2,
    q: 'Are your pieces 100% handmade?',
    a: 'Absolutely. Every piece from Weaves of Lucknow is authentically hand-embroidered by skilled artisans in Lucknow. We never use machine embroidery. Each stitch is placed by human hands — slowly, deliberately, with love.',
  },
  {
    id: 3,
    q: 'How long does it take to make one piece?',
    a: 'Depending on the complexity, a single garment can take anywhere from 40 to 80+ hours of continuous hand embroidery. Intricate suit sets and angarkhas with jaali or crocia work take even longer. That\'s why each piece carries a story.',
  },
  {
    id: 4,
    q: 'What fabrics do you work with?',
    a: 'We work with a curated range: Pure Cotton, Kota Doria, Mal Mal Doria, Chanderi, Silk, Chiffon, Viscose, and seasonal blends. Each fabric is chosen for its breathability, drape, and suitability for hand embroidery.',
  },
  {
    id: 5,
    q: 'How do I order or enquire about a piece?',
    a: 'Join our WhatsApp community to get first access to new collections and DM us directly. For retail and bulk orders, fill out our enquiry form below. We aim to respond within 24 hours.',
  },
  {
    id: 6,
    q: 'Do you accept bulk or custom orders for retailers?',
    a: 'Yes! We partner with boutiques, retailers, and curators across India. We offer flexible MOQ for first-time partners, custom fabric and thread colour options, and dedicated support. Fill out the bulk enquiry form below and let\'s talk.',
  },
  {
    id: 7,
    q: 'Do you ship across India?',
    a: 'Yes, we ship pan-India. International shipping is available on request. All pieces are carefully packaged to protect the embroidery. Join our WhatsApp community for shipping updates and new collection drops.',
  },
];

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } }
};

export default function FAQSection() {
  const [openId, setOpenId] = useState(null);
  const toggle = (id) => setOpenId(openId === id ? null : id);

  return (
    <section data-testid="faq-section" className="py-24 md:py-32 bg-[#E8DED1]">
      <div className="max-w-3xl mx-auto px-6 md:px-12">
        <motion.div
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="text-center mb-14"
        >
          <motion.span variants={fadeUp} className="inline-block text-xs font-sans uppercase tracking-[0.25em] text-[#C9A84C] font-medium mb-4">
            You Asked, We Answered
          </motion.span>
          <motion.h2 variants={fadeUp} className="font-serif font-light text-[#2D4A2D] text-4xl md:text-5xl leading-tight tracking-tight mb-4">
            Your Questions,
            <span className="italic"> Answered</span>
          </motion.h2>
          <motion.p variants={fadeUp} className="font-sans font-light text-[#5C6B5C] text-base">
            Sabse zyada pooche jaane wale sawaalon ke jawab — seedhe aur sachche.
          </motion.p>
        </motion.div>

        <motion.div
          variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.07 } } }}
          initial="hidden" whileInView="visible" viewport={{ once: true }}
          className="space-y-0 border-t border-[#D5CCBF]"
        >
          {faqs.map((faq) => (
            <motion.div key={faq.id} variants={fadeUp} className="border-b border-[#D5CCBF]">
              <button
                onClick={() => toggle(faq.id)}
                data-testid={`faq-item-${faq.id}`}
                className="w-full py-5 flex items-center justify-between text-left gap-4 group"
              >
                <span className={`font-serif text-lg leading-snug transition-colors duration-200 ${openId === faq.id ? 'text-[#2D4A2D]' : 'text-[#1A2A1A] group-hover:text-[#2D4A2D]'}`}>
                  {faq.q}
                </span>
                <ChevronDown
                  size={18}
                  strokeWidth={1.5}
                  className={`flex-shrink-0 text-[#C9A84C] transition-transform duration-300 ${openId === faq.id ? 'rotate-180' : ''}`}
                />
              </button>

              <AnimatePresence>
                {openId === faq.id && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                    className="overflow-hidden"
                  >
                    <p className="pb-6 font-sans font-light text-[#5C6B5C] text-sm leading-relaxed">
                      {faq.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
}
